import { $api } from '@/common/api';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { Organization, Patient, Practitioner, PractitionerRole, User } from '@/fhirworks';
import { useTimeoutFn } from '@vueuse/core';
import { isValid, parseISO } from 'date-fns';
import { isEmpty, upperFirst } from 'lodash';
import { defineStore } from 'pinia';
import VueCookies from 'vue-cookies';

/**
 * Dispatches a CustomEvent to notify browser extensions of authentication state changes.
 * This enables SSO with partner extensions using a secure one-time code exchange.
 *
 * Security model:
 * - We dispatch a one-time SSO code (not the actual token)
 * - The code expires in 30 seconds and can only be redeemed once
 * - Even if XSS steals the code, it's useless after first redemption
 * - 3rd parties call /api/auth/sso/redeem to verify the code server-side
 *
 * @param {string} eventType - The type of auth event: 'login', 'logout', or 'refresh'
 * @param {object|null} authInfo - Non-sensitive auth info (userId, accountId, timestamp)
 * @param {string|null} ssoCode - One-time SSO code for secure verification (optional)
 */
function dispatchAuthEvent(eventType, authInfo = null, ssoCode = null) {
    if ((eventType === 'login' || eventType === 'refresh') && !ssoCode) {
        return;
    }
    const event = new CustomEvent('bestnotes:auth', {
        detail: {
            type: eventType,
            timestamp: Date.now(),
            ssoCode: ssoCode, // One-time use, 30 second expiry
            ...(authInfo || {}),
        },
    });
    window.dispatchEvent(event);
}

export const useAuthStore = defineStore('auth', {
    state() {
        return {
            token: '',
            user: {},
            practitioner: {},
            practitionerRoles: [],
            patient: {},
            portalViewingPatientId: null,
            relatedPersons: [],
            permissions: [],
            organizationAccount: {},
            account: {},
            brand: {},
            userAgencies: [],
            accountsCount: null,
            lockoutLastActivityTime: new Date(),
            lockoutPage: null,
            tokenRefreshTimeMins: 12,
            lastTokenRefreshTime: null,
            loginStatus: {
                loading: false,
                success: false,
                errors: [],
            },
        };
    },
    getters: {
        isAuthenticated: (state) => !!state.token,
        isLockedOut: (state) => !state.token && !isEmpty(state.user),
        hasAccessGroup: (state) => (groups) => {
            if (!groups) return true;

            let assignedAccessGroupIds = state.user.user.accessGroups?.map((group) => group.id) || [];
            if (!Array.isArray(groups)) {
                groups = [groups];
            }
            const found = groups.some((searchGroup) => assignedAccessGroupIds.includes(searchGroup.id));
            return found;
        },
        hasPermission: (state) => (permissions) => {
            // we want to accomplish arrays and a single string.  If none is input.  filter out those below
            if (!Array.isArray(permissions)) {
                permissions = [permissions].filter((perm) => perm);
            }
            return permissions.some((permission) => state.permissions.includes(permission));
        },
        aidboxPersonResource: (state) => {
            return state.practitioner?.id ? state.practitioner : state.patient;
        },
        aidboxPersonReference() {
            return { id: this.aidboxPersonResource?.id, resourceType: this.aidboxPersonResource?.resourceType, display: this.aidboxPersonResource?.fullName };
        },
        fullName() {
            return (this.aidboxPersonResource?.fullName || '').trim();
        },
        firstName() {
            return (this.aidboxPersonResource?.firstName || '').trim();
        },
        lockoutTime: (state) => (state.organizationAccount?.sessionTimeOut ? state.organizationAccount?.sessionTimeOut : 5) * 60000,
        timeSlot: (state) => (state.organizationAccount?.calendarTimeSlot ? state.organizationAccount?.calendarTimeSlot : 30),
        tokenRefreshTime: (state) => state.tokenRefreshTimeMins * 60000,
        clientVocab: (state) => (state.organizationAccount?.clientVocab ? upperFirst(state.organizationAccount?.clientVocab) : 'Patient'),
        inputDateFormat: (state) => (state.organizationAccount?.inputDateFormat ? state.organizationAccount?.inputDateFormat : 'MM/dd/yyyy'),
        outputDateFormat: (state) => (state.organizationAccount?.outputDateFormat ? state.organizationAccount?.outputDateFormat : 'MMM do yyyy'),
        outputTimeFormat: (state) => (state.militaryTime ? ' kk:mm' : ' h:mm a'),
        militaryTime: (state) => (state.organizationAccount?.militaryTime ? state.organizationAccount?.militaryTime : false),
        portalEnabled: (state) => state.account?.portalEnabled ?? false,
        scriptSureEnabled: (state) => state.account?.scriptSureEnabled ?? false,
        showSwitchAccount: (state) => state.accountsCount > 1,
        fhirApiUri: (state) => state.account.fhirApiUri,
        isBnSupport: (state) => {
            let tokenData = JSON.parse(atob(state.token.split('.')[1]));
            return tokenData.isSupport;
        },
        isBnSupportUser: (state) => state.user.user?.id === 'bnsupport-user',

        // portal getters
        portalPatientItems: (state) => {
            // Include the logged-in user as the first item
            const items = [];
            if (state.patient?.id) {
                items.push({
                    relationship: null,
                    patient: state.patient,
                    display: 'Myself',
                });
            }
            // Add all related persons
            items.push(...state.relatedPersons);
            return items;
        },
        portalViewingPatient: (state) => {
            // Check if viewing self
            if (state.portalViewingPatientId === state.patient?.id) {
                return state.patient;
            }
            // Otherwise find in related persons
            return state.relatedPersons.find((item) => item.patient?.id === state.portalViewingPatientId)?.patient;
        },
        patientInquiryStatus() {
            return this.portalViewingPatient?.inquiryStatus;
        },
        patientInquiryAccepted() {
            return this.portalViewingPatient?.inquiryStatus === 'accepted' || !this.portalViewingPatient?.inquiryStatus;
        },
        hasMultipleRelatedPersons: (state) => state.relatedPersons?.length > 0,
    },
    actions: {
        setToken(token, eventType = 'login', ssoCode = null) {
            // Store token in memory only - refresh token is in HttpOnly cookie
            this.token = token;

            // Notify browser extensions of auth state change (for SSO)
            // The ssoCode is provided by the server in the login/refresh response
            try {
                const tokenData = JSON.parse(atob(token.split('.')[1]));
                const authInfo = {
                    authenticated: true,
                    userId: tokenData.box_user,
                    userSystemId: tokenData.sub,
                    userEmail: tokenData.username,
                    accountId: tokenData.aud?.replace('account/', '') || null,
                    permissions: tokenData.permissions,
                };

                dispatchAuthEvent(eventType, authInfo, ssoCode);
            } catch {
                // If token parsing fails, still dispatch event with minimal info
                dispatchAuthEvent(eventType, { authenticated: true }, ssoCode);
            }
        },
        removeToken() {
            this.token = '';

            // Notify browser extensions of logout (for SSO)
            dispatchAuthEvent('logout', { authenticated: false }, null);
        },
        setUser(user) {
            if (user.user && !(user.user instanceof User)) {
                user.user = new User(user.user);
            }

            VueCookies.set('user', JSON.stringify(user));
            this.user = user;
        },
        removeUser() {
            VueCookies.remove('user');
            this.user = {};
        },
        setPractitioner(practitioner) {
            if (practitioner && !(practitioner instanceof Practitioner)) {
                practitioner = new Practitioner(practitioner);
            }

            VueCookies.set('practitioner', JSON.stringify(practitioner));
            this.practitioner = practitioner;
        },
        removePractitioner() {
            VueCookies.remove('practitioner');
            this.practitioner = {};
        },
        // for Patient Portal
        setPatient(patient) {
            if (patient && !(patient instanceof Patient)) {
                patient = new Patient(patient);
            }

            VueCookies.set('patient', JSON.stringify(patient));
            this.patient = patient;
        },
        removePatient() {
            VueCookies.remove('patient');
            this.patient = {};
        },
        setPortalViewingPatientId(patientId) {
            VueCookies.set('portalViewingPatientId', patientId);
            this.portalViewingPatientId = patientId;
        },
        removePortalViewingPatientId() {
            VueCookies.remove('portalViewingPatientId');
            this.portalViewingPatientId = null;
        },
        setPermissions(permissions) {
            if (typeof permissions === 'string') {
                permissions = JSON.parse(permissions);
            }

            this.permissions = Object.values(permissions);
        },
        removePermissions() {
            this.permissions = [];
        },
        setPractitionerRoles(practitionerRoles) {
            if (typeof practitionerRoles === 'string') {
                practitionerRoles = JSON.parse(practitionerRoles);
            }

            localStorage.setItem('practitionerRoles', JSON.stringify(practitionerRoles));
            this.practitionerRoles = practitionerRoles;
        },
        removePractitionerRoles() {
            localStorage.removeItem('practitionerRoles');
            this.practitionerRoles = [];
        },
        setAccountData(account) {
            VueCookies.set('account', JSON.stringify(account));
            this.account = account;
        },
        removeAccount() {
            VueCookies.remove('account');
            this.account = {};
        },
        setBrand(brand) {
            const activeBrand = {
                id: brand.id,
                name: brand.display || brand.name,
                location: brand.location,
            };
            VueCookies.set('brand', JSON.stringify(activeBrand));
            this.brand = activeBrand;
        },
        removeBrand() {
            VueCookies.remove('brand');
            this.brand = {};
        },
        setUserAgencies(userAgencies) {
            localStorage.setItem('userAgencies', JSON.stringify(userAgencies));
            this.userAgencies = userAgencies;
        },
        removeUserAgencies() {
            localStorage.removeItem('userAgencies');
            this.userAgencies = [];
        },
        setOrganizationAccount(account) {
            if (!(account instanceof Organization)) {
                account = new Organization(account);
            }
            VueCookies.set('organizationAccount', JSON.stringify(account));
            this.organizationAccount = account;
        },
        removeOrganizationAccount() {
            VueCookies.remove('organizationAccount');
            this.organizationAccount = {};
        },
        setAccountDetails(details) {
            if (!Array.isArray(details)) details = [details];
            details.forEach((item) => {
                if (item.value) {
                    if (item.key === 'name') {
                        this.account.name = item.value;
                        VueCookies.set('account', JSON.stringify(this.account));

                        this.organizationAccount.name = item.value;
                        VueCookies.set('organizationAccount', JSON.stringify(this.organizationAccount));
                        return;
                    }
                    if (item.key === 'logo') {
                        this.account.logo = item.value;
                        VueCookies.set('account', JSON.stringify(this.account));
                    }
                    this.organizationAccount[item.key] = item.value;
                    VueCookies.set('organizationAccount', JSON.stringify(this.organizationAccount));
                }
            });
        },
        setAccountsCount(count) {
            localStorage.setItem('accounts-count', count);
            this.accountsCount = count;
        },
        removeAccountsCount() {
            localStorage.removeItem('accounts-count');
            this.accountsCount = null;
        },
        setLastTokenRefreshTime(time) {
            if (isValid(time)) {
                localStorage.setItem('lastTokenRefreshTime', time.toISOString());
            }
            this.lastTokenRefreshTime = time;
        },
        removeLastTokenRefreshTime() {
            localStorage.removeItem('lastTokenRefreshTime');
            this.lastTokenRefreshTime = null;
        },
        setLockoutPage(page) {
            localStorage.setItem('lockoutPage', page);
            this.lockoutPage = page;
        },
        removeLockoutPage() {
            localStorage.removeItem('lockoutPage');
            this.lockoutPage = null;
        },
        /**
         * If credentials are provided then login, else...
         * If the user is already authenticated then set the data in the store
         *
         * @param commit
         * @param credentials
         * @returns {Promise<void>}
         */
        async login(credentials) {
            try {
                this.loginStatus.loading = true;
                this.loginStatus.success = false;
                this.loginStatus.errors = [];

                await this.clearAuth(); // Attempt to logout the current user before logging in
                await this.getAuthToken(credentials);
                // await this.logNotice({ summary: 'Login Successful' });

                this.loginStatus.loading = false;
                this.loginStatus.success = true;
            } catch (errs) {
                await this.clearAuth();
                return Promise.reject(errs);
            }
        },

        async refreshCredentials() {
            // Restore cached data from cookies/localStorage
            let user = VueCookies.get('user');
            let practitioner = VueCookies.get('practitioner');
            let patient = VueCookies.get('patient');
            let account = VueCookies.get('account');
            let organizationAccount = VueCookies.get('organizationAccount');
            let brand = VueCookies.get('brand');
            let practitionerRoles = localStorage.getItem('practitionerRoles');
            let lastTokenRefreshTime = localStorage.getItem('lastTokenRefreshTime');
            let accountsCount = localStorage.getItem('accounts-count');
            let userAgencies;
            if (localStorage.getItem('userAgencies')) {
                userAgencies = JSON.parse(localStorage.getItem('userAgencies'));
            }
            let lockoutPage;
            if (localStorage.getItem('lockoutPage')) {
                lockoutPage = localStorage.getItem('lockoutPage');
            }

            // Check for Cypress test token first (bypasses HttpOnly cookie flow)
            let cypressToken = localStorage.getItem('cypress_token');
            if (cypressToken && window.Cypress) {
                this.setToken(cypressToken, 'refresh');
                let tokenData = JSON.parse(atob(cypressToken.split('.')[1]));
                if (tokenData?.permissions) {
                    this.setPermissions(tokenData.permissions);
                }
            } else {
                // Try to get a new access token using the HttpOnly refresh cookie
                try {
                    let res;
                    if (window.page === 'ehr') {
                        res = await $api.auth.refreshToken({ params: { jwtTokenAccount: account.id } });
                    } else {
                        res = await $api.auth.refreshTokenPortal({ params: { jwtTokenAccount: account.id } });
                    }

                    if (res.data.token) {
                        this.setToken(res.data.token, 'refresh');

                        let tokenData = JSON.parse(atob(res.data.token.split('.')[1]));
                        if (tokenData?.permissions) {
                            this.setPermissions(tokenData.permissions);
                        }
                    }
                } catch (error) {
                    // No valid refresh token, and user is not locked out - user needs to log in again
                    if (!(error?.response?.status === 401 && user && lockoutPage)) {
                        // Clear any stale cached data
                        this.clearAuth();
                        return;
                    }
                }
            }

            // Restore cached user/account data
            if (user) {
                this.setUser(user);
            }
            if (lastTokenRefreshTime) {
                this.setLastTokenRefreshTime(parseISO(lastTokenRefreshTime));
            }
            if (practitioner) {
                this.setPractitioner(practitioner);
            }
            if (patient) {
                this.setPatient(patient);
            }
            if (practitionerRoles) {
                this.setPractitionerRoles(practitionerRoles);
            }
            if (account) {
                this.setAccountData(account);
            }
            if (organizationAccount) {
                this.setOrganizationAccount(organizationAccount);
            }
            if (brand && account) {
                this.setBrand(brand);
            }
            if (userAgencies && account) {
                this.setUserAgencies(userAgencies);
            }
            if (accountsCount) {
                this.setAccountsCount(accountsCount);
            }
            if (lockoutPage) {
                this.setLockoutPage(lockoutPage);
            }
        },

        async getAuthToken(credentials) {
            // If account exists send it, so we can get a token tied to the account
            let config = this.account.id ? { params: { jwtTokenAccount: this.account.id }, errorHandle: false } : { errorHandle: false };
            let res;

            if (window.page === 'ehr') {
                res = await $api.auth.login(credentials, config);
            } else {
                res = await $api.auth.portalLogin(credentials, config);
            }
            let token = res?.data?.token;
            let ssoCode = res?.data?.ssoCode || null;

            let tokenData = JSON.parse(atob(token.split('.')[1]));

            this.setToken(token, 'login', ssoCode); // Set the token and dispatch SSO event

            if (tokenData?.permissions) {
                this.setPermissions(tokenData.permissions);
            }

            let now = new Date();
            this.setLastTokenRefreshTime(now);
        },

        async refreshAuthToken() {
            // If user is not authenticated, do nothing
            if (!this.isAuthenticated) {
                return;
            }

            // make sure this.account.id is set IF tokenData has account inside
            let existingTokenData = JSON.parse(atob(this.token.split('.')[1]));
            if (existingTokenData?.aud && !existingTokenData?.aud?.includes(this.account.id)) {
                return;
            }

            let config = this.account.id ? { params: { jwtTokenAccount: this.account.id } } : null;

            let res;
            if (window.page === 'ehr') {
                res = await $api.auth.refreshToken(config);
            } else {
                res = await $api.auth.refreshTokenPortal(config);
            }
            let token = res.data.token;
            let ssoCode = res.data.ssoCode || null;

            if (token) {
                let now = new Date();

                let tokenData = JSON.parse(atob(token.split('.')[1]));

                this.setToken(token, 'refresh', ssoCode);
                if (tokenData?.permissions) {
                    this.setPermissions(tokenData.permissions);
                }
                this.setLastTokenRefreshTime(now);
            }
        },

        async getMeUser() {
            let aidboxUserResources;

            let tokenData = JSON.parse(atob(this.token.split('.')[1]));

            let apiUser = {
                email: tokenData.username,
                id: tokenData.sub,
                isBnSupport: tokenData?.isSupport,
                videraEnabled: tokenData?.videraEnabled,
                videraUserId: tokenData?.videraUserId,
            };

            if (window.page === 'ehr') {
                aidboxUserResources = await $api.users.getMe(apiUser.id);
            } else {
                aidboxUserResources = await $api.users.getMePortal(apiUser.id);
            }

            let storeUser = { apiUser: apiUser };

            if (window.page === 'ehr') {
                let practitioner, practitionerRoles;

                if (aidboxUserResources?.length) {
                    let userResource = aidboxUserResources.find((entry) => entry.resourceType === 'User');

                    let practitionerResource = aidboxUserResources.find((entry) => entry.resourceType === 'Practitioner');
                    let practitionerRoleResources = aidboxUserResources.filter((entry) => entry.resourceType === 'PractitionerRole');

                    if (userResource) {
                        storeUser.user = userResource;
                    }
                    if (practitionerResource) {
                        practitioner = practitionerResource;
                    }
                    if (practitionerRoleResources) {
                        if (!(practitionerRoleResources[0] instanceof PractitionerRole)) {
                            practitionerRoleResources = practitionerRoleResources.map((role) => new PractitionerRole(role));
                        }

                        practitionerRoles = practitionerRoleResources.map((role) => ({
                            programId: role.healthcareService[0]?.id,
                            locationId: role.location[0]?.id,
                            inquiry: role?.healthcareServiceAccess[0]?.inquiry,
                            active: role?.healthcareServiceAccess[0]?.active,
                            past: role?.healthcareServiceAccess[0]?.past,
                            onlyCareTeam: role?.healthcareServiceAccess[0]?.onlyCareTeam,
                        }));

                        practitionerRoleResources.forEach((role) => {
                            role.healthcareService.forEach((hcs) => {
                                practitionerRoles.push({
                                    programId: hcs.id,
                                    locationId: role.location[0].id,
                                    inquiry: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.inquiry,
                                    active: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.active,
                                    past: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.past,
                                    onlyCareTeam: role?.healthcareServiceAccess.find((hsa) => hsa.healthcareService === hcs.id)?.onlyCareTeam,
                                });
                            });
                        });

                        const primaryLocation = practitionerResource?.primaryLocation;
                        const primaryLocationBrand = practitionerResource?.primaryLocationBrand;
                        const location = aidboxUserResources.find((item) => item.id === primaryLocation?.id) || aidboxUserResources.find((item) => item.resourceType === 'Location');
                        const brand = aidboxUserResources.find((item) => item.id === primaryLocationBrand?.id) || aidboxUserResources.find((item) => item.id === location?.managingOrganization?.id);

                        let brandLocation;
                        let brands = [];

                        let userBrands = aidboxUserResources.filter((item) => item.resourceType === 'Organization').sort((a, b) => (a.name > b.name ? 1 : -1));

                        userBrands?.forEach((brand) => {
                            let locations = [];
                            aidboxUserResources
                                .filter((item) => item.organization?.id === brand.id)
                                .sort((a, b) => (a.name > b.name ? 1 : -1))
                                ?.forEach((item) => {
                                    const locationRef = item.location[0];
                                    // Find the full location resource to get extensions like scriptSurePracticeId
                                    const fullLocation = aidboxUserResources.find((res) => res.resourceType === 'Location' && res.id === locationRef.id);
                                    locations.push({
                                        id: locationRef.id,
                                        display: locationRef.display,
                                        resourceType: 'Location',
                                        scriptSurePracticeId: fullLocation?.scriptSurePracticeId,
                                    });
                                });
                            brands.push({ id: brand.id, name: brand.name, resourceType: 'Organization', locations: locations.sort((a, b) => (a.display > b.dispaly ? 1 : -1)) });
                        });

                        if (primaryLocationBrand?.id && brand?.id && location?.id) {
                            brandLocation = {
                                id: brand.id,
                                name: brand.name,
                                resourceType: brand.resourceType,
                                location: {
                                    id: location.id,
                                    display: location.name,
                                    resourceType: location.resourceType,
                                    scriptSurePracticeId: location.scriptSurePracticeId,
                                },
                            };
                        } else if (brands.length) {
                            const defaultBrand = brands[0];
                            const defaultLocation = brands[0].locations[0];
                            brandLocation = {
                                id: defaultBrand.id,
                                name: defaultBrand.name,
                                resourceType: defaultBrand.resourceType,
                                location: {
                                    id: defaultLocation.id,
                                    display: defaultLocation.display,
                                    resourceType: defaultLocation.resourceType,
                                    scriptSurePracticeId: defaultLocation.scriptSurePracticeId,
                                },
                            };
                        }

                        if (brandLocation) {
                            this.setBrand(brandLocation);
                            this.setUserAgencies(brands);
                        }
                    }
                } else if (storeUser.apiUser.isBnSupport) {
                    let location = null,
                        brand = null,
                        healthcareService = null;

                    let locations = await $httpFhirApi.get('Location?status=active');
                    if (locations.status == 200) {
                        let locationsData = locations.data;
                        // Done for dev purposes otherwise 1st record will be used
                        let devLocation = locationsData.find((item) => item.id === 'dev-location');
                        if (!devLocation) {
                            devLocation = locationsData[0];
                        }
                        // Extract the location, brand and healthcareService from the devLocation
                        location = devLocation;
                        brand = devLocation.managingOrganization;
                        healthcareService = devLocation.defaultProgram;
                    }

                    // Call to setBrand need to make autho stuff work?
                    this.setBrand({
                        id: location.managingOrganization.id,
                        name: location.managingOrganization.display,
                        location: { id: location.id, display: location.name, resourceType: location.resourceType },
                    });

                    storeUser.user = { id: 'bnsupport-user' };
                    practitioner = { id: 'bnsupport-practitioner', name: [{ use: 'official', given: ['bnsupport'] }] };
                    practitionerRoles = null;
                }

                this.setUser(storeUser); // Set the token before calling the getMe api endpoint
                this.setPractitioner(practitioner);
                this.setPractitionerRoles(practitionerRoles);
            } else {
                // Is portal user
                let patient;

                if (aidboxUserResources?.length) {
                    let userResource = aidboxUserResources.find((entry) => entry.resourceType === 'User');
                    let patientResource = aidboxUserResources.find((entry) => entry.resourceType === 'Patient');

                    if (userResource) {
                        storeUser.user = userResource;
                    }
                    if (patientResource) {
                        patient = patientResource;
                    }

                    this.setUser(storeUser); // Set the token before calling the getMe api endpoint
                    this.setPatient(patient);
                }
            }
        },

        async clearRefreshToken() {
            try {
                if (window.page === 'ehr') {
                    await $api.auth.logout();
                } else {
                    await $api.auth.logoutPortal();
                }
            } catch {
                // Ignore errors - we're logging out anyway
            }
        },

        async clearAuth(switchingAccount = false) {
            // Call logout endpoint to clear the HttpOnly refresh token cookie
            if (!switchingAccount && this.token) {
                await this.clearRefreshToken();
            }

            if (!switchingAccount) {
                this.removeToken();
                this.removeUser();
                // account needs to be here so we don't get logged out to login page when switching accounts
                this.removeAccount();
                this.removeOrganizationAccount();
                this.removeAccountsCount();
                this.removePractitioner();
                this.removePractitionerRoles();
                this.removePatient();
            }
            this.removeBrand();
            this.removeUserAgencies();
            this.removePermissions();
            this.removeLockoutPage();
            this.removeLastTokenRefreshTime();
        },

        resetLockoutActivityTime() {
            let time = new Date();
            localStorage.setItem('lockoutLastActivityTime', time.toISOString());
            this.lockoutLastActivityTime = time;
        },

        setLockoutLastActivityTime(time) {
            this.lockoutLastActivityTime = time;
        },

        async lockScreen(lockoutPage) {
            this.setLockoutLastActivityTime(null);

            // Create a new object using the lockout page from state or the provided one
            lockoutPage = this.lockoutPage || lockoutPage;

            if (!lockoutPage || lockoutPage.includes('lockout')) {
                lockoutPage = '/dashboard';
            }

            this.setLockoutPage(lockoutPage);
            // allow time for background requests to complete (save state) before removing token
            // there may be a better way to do this... Justin?
            useTimeoutFn(() => {
                this.clearRefreshToken();
                this.removeToken();
            }, 3000);
        },

        async unlockScreen(password) {
            this.resetLockoutActivityTime();
            await this.getAuthToken({
                email: this.user.apiUser.email,
                password: password,
            });
            if (!this.isLockedOut) {
                this.removeLockoutPage();
            }
        },

        async setAccount(account) {
            this.setAccountData(account);
            await this.refreshAuthToken();
            let organizationAccount = await $httpFhirApi.get('Organization/' + account.id);
            if (organizationAccount?.data) {
                this.setOrganizationAccount(organizationAccount.data);
            }
        },
        async requestPasswordResetAuth(data) {
            return await $api.auth.requestResetPassword(data);
        },
        async requestPasswordResetPortalAuth(data) {
            return await $api.auth.requestResetPasswordPortal(data);
        },
        async passwordResetPortalAuth(data) {
            return await $api.auth.resetPasswordPortal(data);
        },
        async passwordResetAuth(data) {
            return await $api.auth.resetPassword(data);
        },

        async refreshRelatedPersons() {
            if (!this.patient?.id) {
                this.relatedPersons = [];
                return;
            }

            try {
                const response = await $httpFhirApi.get(`/BN_Relationship?related=${this.patient.id}&portalAccess=true&active=true&_include=BN_Relationship:subject`);

                const relationships = response.data?.filter((item) => item.resourceType === 'BN_Relationship') || [];
                const patients = response.data?.filter((item) => item.resourceType === 'Patient') || [];

                this.relatedPersons = relationships.map((rel) => {
                    const relatedPatient = patients.find((p) => p.id === rel.subject?.id);
                    return {
                        relationship: rel,
                        patient: relatedPatient instanceof Patient ? relatedPatient : new Patient(relatedPatient),
                        display: relatedPatient?.fullName || 'Unknown',
                    };
                });
            } catch (error) {
                console.error('Error refreshing related persons:', error);
                this.relatedPersons = [];
            }
        },

        async loadRelatedPersons() {
            // Refresh the related persons list
            await this.refreshRelatedPersons();

            // Set initial viewing patient if not already set
            if (isEmpty(this.portalViewingPatient) && this.relatedPersons.length >= 0) {
                // Priority: accepted patient (self) > accepted related > pending related > any patient (self)
                const viewingPatientId =
                    (this.patient?.inquiryStatus === 'accepted' && this.patient?.id) ||
                    this.relatedPersons.find((item) => item.patient?.inquiryStatus === 'accepted')?.patient?.id ||
                    this.relatedPersons.find((item) => item.patient?.inquiryStatus === 'pending')?.patient?.id ||
                    this.patient?.id;

                this.setPortalViewingPatientId(viewingPatientId);
            }
        },
    },
});
